/**
 * CommunityLayout — path-based (/c/:slug/...) white-label wrapper.
 *
 * Fetches the tenant's public site config once and exposes it to every child
 * route via CommunityContext. This is the scaffold that lets future
 * community-scoped surfaces (portal, share links, board pages, etc.) slot in
 * under /c/:slug/... without duplicated branding-lookup plumbing.
 *
 * Behavior:
 *  - On 404 slug: renders the same "This community page isn't live yet"
 *    screen used by the /c/:slug landing page.
 *  - On fetch: renders <Outlet /> with { slug, site } in context.
 *  - Preserves existing root-level routes (/portal, /share/:token, ...) — this
 *    layout is additive.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, useParams, Link } from "react-router-dom";
import axios from "axios";
import { useBrandTheme } from "@/lib/brand";
import { Waves } from "lucide-react";
import { BrandFont } from "@/components/BrandFont";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityContext = createContext({ slug: null, site: null });

export function useCommunity() {
  return useContext(CommunityContext);
}

export default function CommunityLayout() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!slug) { setMissing(true); return; }
    setSite(null);
    setMissing(false);
    axios
      .get(`${API}/public/site/${slug}`)
      .then((r) => setSite(r.data))
      .catch(() => setMissing(true));
  }, [slug]);
  // One colour for everything under /c/:slug — landing page, sign-in, portal, share and sign links.
  useBrandTheme(site?.brand_color);

  if (missing) {
    return (
      <div
        data-testid="community-not-live"
        className="min-h-screen bg-[#1a1614] text-white flex flex-col items-center justify-center gap-3 p-8 text-center font-body"
      >
        <Waves className="h-10 w-10 text-redline-500" />
        <h1 className="font-heading text-2xl font-extrabold">This community page isn&apos;t live yet</h1>
        <p className="text-sand-400 text-sm max-w-sm">Check the address, or ask your community manager to publish their page on Ask Redline.</p>
        <Link to="/" className="mt-2 rounded-full bg-redline-500 hover:bg-redline-600 transition-colors px-6 py-3 font-bold text-sm">What&apos;s Ask Redline?</Link>
      </div>
    );
  }

  if (!site) {
    return (
      <div
        data-testid="community-loading"
        className="min-h-screen bg-[#f7f4f0] flex items-center justify-center"
      >
        <div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <CommunityContext.Provider value={{ slug, site }}>
      <div data-brandfont>
        <BrandFont brand={site} />
        <Outlet />
      </div>
    </CommunityContext.Provider>
  );
}
